import React, { useRef, useState } from 'react';
import { Router } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Fuse,
  printMissingConfigError,
  SiteConfigManager,
  StaticBlocksProvider,
  StorageManager,
  useApplicationState,
} from '@atlas/core';
import { COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { Loader } from '@atlas/ui';

import { TERMINAL_STORAGE_KEY } from 'common/constants';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import history from 'components/Layout/history';
import LayoutErrorFallback from 'components/Layout/LayoutErrorFallback';
import addBetBoosterWindowFunctions from 'components/Sportsbook/BetBooster/addBetBoosterWindowFunctions';
import { BetBoosterGlobalContextProvider } from 'components/Sportsbook/BetBooster/BetBoosterGlobalContext';
import useThemeBootstrap from 'hooks/useThemeBootstrap';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';
import Initialization from 'src/terminal/components/Initialization/Initialization';
import StillHereComponent from 'src/terminal/components/StillHereComponent/StillHereComponent';
import useRedirectAfterDeposit from 'src/hooks/useRedirectAfterDeposit';

import Header from './Header';
import QueryRoutes from './QueryRoutes';

const { deposit } = paymentRoutes;
const { CLIENT_PROFILE } = COMPONENT_TYPES;

const AuthorizationLoader = ({ children }) => {
  const { resolved } = useApplicationState();

  if (resolved) {
    return children;
  }

  return <Loader className="page__main" />;
};

AuthorizationLoader.propTypes = {
  children: PropTypes.node,
};

const RootLayout = ({ children }) => {
  const headerRef = useRef(null);
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const pages = SiteConfigManager.getConfig('config.pages', []);
  const [terminalKey, setTerminalKey] = useState(StorageManager.getItem(TERMINAL_STORAGE_KEY));

  if (pages.length === 0) {
    printMissingConfigError('config.pages');
  }

  addBetBoosterWindowFunctions();
  useThemeBootstrap();
  useRedirectAfterDeposit();

  if (!terminalKey) {
    return <Initialization setTerminalKey={setTerminalKey} />;
  }

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <Router history={history}>
        <StaticBlocksProvider>
          <BetBoosterGlobalContextProvider>
            <CustomStylesPortal />
            <Header
              depositBtnLink={`${clientProfilePath}/${deposit}`}
              isIgnoredUnreadMessages={true}
              logoutRedirectLink="/"
              ref={headerRef}
            />

            <main className="page">
              <AuthorizationLoader>{children}</AuthorizationLoader>
              <DebugInfo />
            </main>
            <div id="legitimuz-root" />

            <StillHereComponent />
            <QueryRoutes />
          </BetBoosterGlobalContextProvider>
        </StaticBlocksProvider>
      </Router>
    </Fuse>
  );
};

RootLayout.propTypes = {
  children: PropTypes.node,
};

export default RootLayout;
